import { NavigationService } from './../../shared/services/navigation.service';
import { ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from "@angular/router";
import { UsersService } from 'src/app/users/users.service';
import { EnterpriseService } from "../enterprise.service";
import { NotificationMessageService } from '../../shared/notification-message/notification-message.service';
import { SideMenuService } from 'src/app/shared/side-menu/side-menu.service';
import { PermissionsService } from 'src/app/permissions/permissions.service';
import { SitesService } from 'src/app/sites/sites.service';
import { CommonDataService } from 'src/app/shared/services/common-data.service';
import { CommonService } from 'src/app/shared/services/common.service';
import { TranslateService } from '@ngx-translate/core';
import { ModalComponent } from 'src/app/shared/modal/modal.component';

@Component({
	selector: 'app-enterprise-edit',
	templateUrl: './enterprise-edit.component.html',
	styleUrls: ['./enterprise-edit.component.css']
})
export class EnterpriseEditComponent implements OnInit {
	@ViewChild('twoSVModal') twoSVModal: ModalComponent;
	@ViewChild('forcePasswordChangeModal') forcePasswordChangeModal: ModalComponent;
	private forcePasswordChangeSubscribed = false;
	
	originalPermissionId: number;
	previousTwoSVState: boolean = false;

	readonly TWO_SV_ENABLED_OPTIONS = this.usersService.TWO_SV_ENABLED_OPTIONS;
	twoSVChoice: typeof this.TWO_SV_ENABLED_OPTIONS[keyof typeof this.TWO_SV_ENABLED_OPTIONS] | null = null;
	tempTwoSVChoice: typeof this.TWO_SV_ENABLED_OPTIONS[keyof typeof this.TWO_SV_ENABLED_OPTIONS] = this.TWO_SV_ENABLED_OPTIONS.NONE;

	groupTypes = [];
	permissionList: any = [];
	map: any = {};
	timeZonesMenu: any = [];
	showMapBox: boolean = false;
	currentUser: any;
	addressEditable = false;
	@Input() enterprise: any = {
		id: 0,
		name: '',
		admins:[],
		group_type: null,
		default_permission_id: null,
		update_linked: false,
		auto_generate_customer: true,
		is_two_sv_enabled: false,
		site: {}
	};
	@Input() isModal: boolean = false;
	@Output() formSubmitted = new EventEmitter<Object>();
	@Output() formCancelled = new EventEmitter<void>();

	constructor(
		private router: Router,
		private route: ActivatedRoute,
		private chRef: ChangeDetectorRef,
		private usersService: UsersService,
		private enterpriseService: EnterpriseService,
		private permissionsService: PermissionsService,
		private sitesService: SitesService,
		private notificationMessage: NotificationMessageService,
		private sideMenuService: SideMenuService,
		private commonData: CommonDataService,
		private navigation: NavigationService,
		private commonService: CommonService,
		private translateService: TranslateService
	) {
		this.map = {...this.sitesService.defaultMapLocation};
	}

	ngOnInit() {
		this.previousTwoSVState = this.enterprise.is_two_sv_enabled;
		this.tempTwoSVChoice = this.TWO_SV_ENABLED_OPTIONS.NONE;

		this.sideMenuService.hide();
		this.currentUser = this.usersService.getCurrentUser();
		if (!this.currentUser.isSCT || !this.usersService.hasAccessFunction('enterprise_management')) {
			this.notificationMessage.setMessage('globalAuthMsg');
			this.router.navigate(['/'], {});
		}
		this.route.params.subscribe(
			params => {
				this.enterprise['id'] = Number(params['enterpriseId']);
				this.commonService.setPageTabTitle(`${this.translateService.instant('g.add_new')} ${this.translateService.instant('ent.enterprise')}`);
				if(this.enterprise['id'])
					this.getEnterprise(this.enterprise['id']);
			}
		);
		this.groupTypes = this.enterpriseService.getGroupTypes();
		this.getPermissionsList();

		if(!this.enterprise.id) {
			this.timeZonesMenu = this.commonData['TimeZonesMenu'];
			this.showMapBox = true;
			this.enterprise.site.address_manually_edited = false;
		}

	}

	getEnterprise(id) {
		this.enterpriseService.getEnterprise(id, { getEnterpriseUsersCount: true }).subscribe(
			(data: any) => {
				this.enterprise = data;
				this.originalPermissionId = data.default_permission_id;
				this.previousTwoSVState = data.is_two_sv_enabled;
				this.commonService.setPageTabTitle(`${this.translateService.instant('g.edit')} - ${this.enterprise.name}`);
			}
		);
	}

	onSubmit() {
		// Check if we need to show the 2SV disable confirmation
		if (!this.enterprise.is_two_sv_enabled && this.previousTwoSVState && this.enterprise.users_count) {
			this.tempTwoSVChoice = this.TWO_SV_ENABLED_OPTIONS.NONE;
			this.twoSVModal.show();

			// Subscribe to modal close event
			this.twoSVModal.onClose.subscribe((result: boolean) => {
				if (result) {
					// User confirmed, save with the selected choice
					this.twoSVChoice = this.tempTwoSVChoice;
					this.saveEnterprise();
				}
				// If cancelled, just close the modal and keep the form open
			});
		} else {
			// No confirmation needed, save directly
			this.saveEnterprise();
		}
	}

	openForcePasswordChangeModal() {
		this.forcePasswordChangeModal.show();

		//subscribe once, otherwise re-opening the modal would fire the request several times
		if (this.forcePasswordChangeSubscribed)
			return;

		this.forcePasswordChangeSubscribed = true;
		this.forcePasswordChangeModal.onClose.subscribe((confirmed: boolean) => {
			if (confirmed)
				this.forceUsersPasswordChange();
		});
	}

	forceUsersPasswordChange() {
		this.enterpriseService.forcePasswordChange(this.enterprise.id).subscribe(
			(data: any) => {
				if (!data || data.affected_users === undefined)
					return this.notificationMessage.setMessage('globalErrMsg', {clearOnXTimeNavigate: 1});

				const message = this.translateService.instant('ent.force_password_change_done', {count: data.affected_users});
				this.notificationMessage.setMessage(message, {type: 'success'});
			}
		);
	}

	saveEnterprise() {
		// Add twoSVChoice to the enterprise object if it exists
		if (this.twoSVChoice)
			this.enterprise.two_sv_enabled_by = this.twoSVChoice;

		this.enterpriseService.saveEnterprise(this.enterprise).subscribe(
			(data: any)=> {
				if(this.isModal)
					return this.formSubmitted.emit({enterpriseId: data});
				if(data) {
					if (data?.api_status) {
						switch(data.api_status){
							case 2:
								return this.notificationMessage.setMessage('translate|customer.customer_name_already_exist');
								break;
							case 3:
								return this.notificationMessage.setMessage('translate|site.site_name_already_exist');
								break;
						}
					}
					this.notificationMessage.setMessage('globalSuccessMsg');
					this.goBack();
				} else {
					this.notificationMessage.setMessage('globalErrMsg', {clearOnXTimeNavigate: 1});
				}
			}
		);
	}

	enableAddressEdit() {
		this.addressEditable = true;
		this.enterprise.site.address_manually_edited = true;
	}

	getPermissionsList() {
		this.permissionsService.getPermissionsList('enterprise').subscribe((data) => {
			this.permissionList = data;
		});
	}

	updateSiteCoords(coords) {
		if(coords.selectedFromMapbox)
			this.enterprise.site.address_manually_edited = false;
		this.enterprise.site['latitude'] = coords['lat'];
		this.enterprise.site['longitude'] = coords['lng'];
		this.enterprise.site['address'] = coords['address'];
		this.enterprise.site['country_id'] = coords['country'];
		this.enterprise.site['state_id'] = coords['state'];
		this.enterprise.site['city'] = coords['city'];
		this.chRef.detectChanges();
	}

	goBack() {
		if(this.isModal)
			return this.formCancelled.emit();

		this.navigation.goBack('/enterprise');
	}
}
